import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import { successToast, errorToast } from "../Utils/toast";
import {
  updatePermission,
  getAllPermission,
  getVendorRequirementPermission,
} from "../redux/services/PermissionService/permission.Services";

const toCamelCase = (str) => {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/^[A-Z]/, (chr) => chr.toLowerCase());
};

function PermissionPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [permissionsList, setPermissionsList] = useState([]);
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [formData, setFormData] = useState({
    vendor_id: id || "",
    permissions: {},
  });
  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoading(true);
        const res = await getAllPermission(); 
        const modules = res?.data || [];
        const list = modules.map((module) => {
          const item = {
            label: module.name,
            key: toCamelCase(module.name),
            module_id: module.id,
          };
          if (module.categories && module.categories.length > 0) {
            item.children = module.categories.map((cat) => ({
              label: cat.name,
              key: toCamelCase(cat.slug),
              category_id: cat.id,
              subcategories: cat.subcategories || [],
            }));
          }
          return item;
        });
        setPermissionsList(list);
        const defaults = list.reduce((acc, item) => {
          acc[item.key] = 0;
          if (item.children) {
            item.children.forEach((child) => {
              acc[child.key] = 0;
              child.subcategories?.forEach((sub) => {
                acc[toCamelCase(sub.slug || sub.name)] = 0;
              });
            });
          }
          return acc;
        }, {});
        setDefaultPermissions(defaults);
        setFormData((prev) => ({
          ...prev,
          permissions: { ...defaults },
        }));
      } catch (error) {
        errorToast("Failed to load modules");
      } finally {
        setLoading(false);
      }
    };
    fetchModules();
  }, []);
  useEffect(() => {
    if (!id || permissionsList.length === 0) return;
    const fetchVendorPermissions = async () => {
      try {
        setLoading(true);
        const res = await getVendorRequirementPermission({
          vendor_id: Number(id),
        });
        const entries = res?.data || [];
        const perms = {};
        permissionsList.forEach((item) => {
          perms[item.key] = 0;
          item.children?.forEach((child) => {
            perms[child.key] = 0;
            child.subcategories?.forEach((sub) => {
              perms[toCamelCase(sub.slug || sub.name)] = 0;
            });
          });
        });
        entries.forEach((p) => {
          const mod = permissionsList.find(
            (m) => m.module_id === p.module_id
          );
          if (!mod) return;
          if (p.subcategory_id) {
            const cat = mod.children?.find(
              (c) => c.category_id === p.category_id
            );
            const sub = cat?.subcategories?.find(
              (s) => s.id === p.subcategory_id
            );
            if (sub) perms[toCamelCase(sub.slug || sub.name)] = 1;
          } else if (p.category_id) {
            const cat = mod.children?.find(
              (c) => c.category_id === p.category_id
            );
            if (cat) perms[cat.key] = 1;
          } else {
            perms[mod.key] = 1;
          }
        });

        setFormData({ vendor_id: id, permissions: perms });
      } catch (e) {
        errorToast("Failed to load permissions");
      } finally {
        setLoading(false);
      }
    };

    fetchVendorPermissions();
  }, [id, permissionsList]);

  const handlePermissionTogglecheck = (key, children = []) => {
    setFormData((prev) => {
      const updatedPermissions = { ...prev.permissions };
      const isChecked = updatedPermissions[key] === 1 ? 0 : 1;

      if (children.length > 0) {
        updatedPermissions[key] = isChecked;
        children.forEach((child) => {
          updatedPermissions[child.key] = isChecked;
          child.subcategories?.forEach((sub) => {
            updatedPermissions[toCamelCase(sub.slug || sub.name)] = isChecked;
          });
        });
      } else {
        updatedPermissions[key] = isChecked;
        const parentItem = permissionsList.find((item) =>
          item.children?.some((child) => child.key === key)
        );
        if (parentItem) {
          const allChildrenChecked = parentItem.children.every(
            (child) => updatedPermissions[child.key] === 1
          );
          const anyChildChecked = parentItem.children.some(
            (child) => updatedPermissions[child.key] === 1
          );
          if (allChildrenChecked) {
            updatedPermissions[parentItem.key] = 1;
          } else if (!anyChildChecked) {
            updatedPermissions[parentItem.key] = 0;
          }
        }
      }

      return { ...prev, permissions: updatedPermissions };
    });
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setFormData((prev) => {
      const updatedPermissions = { ...prev.permissions };
      Object.keys(updatedPermissions).forEach((key) => {
        updatedPermissions[key] = isChecked ? 1 : 0;
      });
      return { ...prev, permissions: updatedPermissions };
    });
  };

  const isAllSelected = () =>
    Object.values(formData.permissions).every((val) => val === 1);

  const isAnySelected = () =>
    Object.values(formData.permissions).some((val) => val === 1);

  const getParentCheckState = (parentKey) => {
    const parentItem = permissionsList.find((item) => item.key === parentKey);
    if (!parentItem?.children) return false;

    const children = parentItem.children;
    const checkedCount = children.filter(
      (child) => formData.permissions[child.key] === 1
    ).length;

    if (checkedCount === 0) return false;
    if (checkedCount === children.length) return true;
    return "indeterminate";
  };

  // -------------------------------
  // 4. Submit
  // -------------------------------
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const permissionsArray = [];

      permissionsList.forEach((module) => {
        if (formData.permissions[module.key] === 1) {
          permissionsArray.push({ module_id: module.module_id });
        }

        if (module.children) {
          module.children.forEach((child) => {
            if (formData.permissions[child.key] === 1) {
              permissionsArray.push({
                module_id: module.module_id,
                category_id: child.category_id,
              });
            }

            if (child.subcategories) {
              child.subcategories.forEach((sub) => {
                const subKey = toCamelCase(sub.slug || sub.name);
                if (formData.permissions[subKey] === 1) {
                  permissionsArray.push({
                    module_id: module.module_id,
                    category_id: child.category_id,
                    subcategory_id: sub.id,
                  });
                }
              });
            }
          });
        }
      });

      const payload = {
        vendor_id: Number(id),
        permissions: permissionsArray,
      };

      console.log("Payload:", payload);

      const res = await updatePermission(payload);

      if (res?.success) {
        successToast("Permissions updated");
        navigate(-1);
      } else {
        errorToast(res?.message || "Failed");
      }
    } catch (error) {
      errorToast(
        error?.response?.data?.message || "Failed to update permissions"
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading && permissionsList.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between page-header-breadcrumb">
        <div className="title_member">
          <h4>Manage Permissions</h4>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft />
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSubmit}
            disabled={loading}
          >
            <FaSave /> {loading ? "Saving..." : "Save Permissions"}
          </button>
        </div>
      </div>

      <div className="main_page">
        <div className="col-12">
          <div className="d-flex align-items-end justify-content-end mb-3">
            <div className="checkallselectall">
              <input
                type="checkbox"
                id="checkall"
                onChange={handleSelectAll}
                checked={isAllSelected()}
                ref={(el) => {
                  if (el) {
                    el.indeterminate = !isAllSelected() && isAnySelected();
                  }
                }}
              />
              <label htmlFor="checkall" className="ms-1">
                All Select
              </label>
            </div>
          </div>
          <div className="row">
            {permissionsList.map((item,index) => (
            <div key={item.key} className="col-xl-3 col-lg-4 col-md-6 mb-4">
              <div className="card shadow-sm border-0 position-relative h-100">
  {/* <span
    className="position-absolute top-0 end-0 p-3 fw-bold text-muted opacity-25"
    style={{ fontSize: "1.5rem" }}
  >
    {String(index + 1).padStart(2, "0")}
  </span> */}
  <div className="card-header bg-light d-flex align-items-center">
                    <div className="form-check m-0 w-100">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`perm-${item.key}`}
                        checked={formData.permissions[item.key] === 1}
                        ref={(el) => {
                          if (el && item.children) {
                            const state = getParentCheckState(item.key);
                            el.indeterminate = state === "indeterminate";
                          }
                        }}
                        onChange={() =>
                          handlePermissionTogglecheck(
                            item.key,
                            item.children || []
                          )
                        }
                      />
                      <label
                        className="form-check-label fw-bold ms-2"
                        htmlFor={`perm-${item.key}`}
                      >
                        {item.label}
                      </label>
                    </div>
                  </div>

                  {item.children?.length > 0 && (
                  <div className="card-body py-2">
  <div className="ps-2 border-start" style={{ maxHeight: "200px", overflowY: "auto" }}>
                        {item.children.map((child) => (
                          <div key={child.key} className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`perm-${child.key}`}
                              checked={formData.permissions[child.key] === 1}
                              onChange={() =>
                                handlePermissionTogglecheck(child.key, [])
                              }
                            />
                            <label
                              className="form-check-label text-light ms-2"
                              htmlFor={`perm-${child.key}`}
                            >
                              {child.label}
                            </label>

                            {child.subcategories?.length > 0 && (
                              <div className="ps-4 mt-1">
                                {child.subcategories.map((sub) => {
                                  const subKey = toCamelCase(
                                    sub.slug || sub.name
                                  );
                                  return (
                                    <div key={subKey} className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`perm-${subKey}`}
                                        checked={
                                          formData.permissions[subKey] === 1
                                        }
                                        onChange={() =>
                                          handlePermissionTogglecheck(
                                            subKey,
                                            []
                                          )
                                        }
                                      />
                                      <label
                                        className="form-check-label text-light ms-2"
                                        htmlFor={`perm-${subKey}`}
                                      >
                                        {sub.name}
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PermissionPage;